"use client";

import { AssetControl, AssetControlProps } from "@zapier/product-nav";
import { Suspense, useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  canvasAtom,
  canvasMetaSetter,
  createNotificationSetter,
  isDeleteCanvasModalOpenAtom,
  isDraftModeExcludingTemplatesAtom,
  isEditDescriptionModalOpenAtom,
  ownerAtomFamily_SUSPENDS,
} from "@zapier/canvas-state";
import { SkeletonBlock } from "@zapier/design-system";

type Props = {
  canvasName: string;
  permissions: AssetControlProps<Record<string, unknown>>["permissions"];
  ownerId: number;
};

const NAME_MAX_LENGTH = 255;

export function InteractiveName({ ownerId, canvasName, permissions }: Props) {
  return (
    <Suspense fallback={<SkeletonBlock height={25} width={25} />}>
      <SuspendedInteractiveName
        canvasName={canvasName}
        ownerId={ownerId}
        permissions={permissions}
      />
    </Suspense>
  );
}

function SuspendedInteractiveName({
  canvasName,
  ownerId,
  permissions = "edit",
}: Props) {
  const owner = useAtomValue(ownerAtomFamily_SUSPENDS(ownerId));
  const ownerName = owner?.full_name;
  const photoUrl = owner?.photo_url;
  const saveMeta = useSetAtom(canvasMetaSetter);
  const canvas = useAtomValue(canvasAtom);
  const setIsEditDescriptionModalOpen = useSetAtom(
    isEditDescriptionModalOpenAtom,
  );

  const setIsDeleteCanvasModalOpen = useSetAtom(isDeleteCanvasModalOpenAtom);

  // Check if AI Copilot banner is visible to show "Unsaved Plan" instead of "untitled"
  const isDraftModeNotTemplates = useAtomValue(
    isDraftModeExcludingTemplatesAtom,
  );

  const createNotification = useSetAtom(createNotificationSetter);
  const handleSave = useCallback(
    async (updatedCanvasName: string) => {
      try {
        await saveMeta({
          name: updatedCanvasName.trim(),
        });

        createNotification({
          type: "success",
          message: "The name of this Canvas has been updated",
        });
      } catch {
        createNotification({
          type: "error",
          message: "There was an issue saving your changes. Please try again.",
        });
      }
    },
    [createNotification, saveMeta],
  );

  const menuItems = [
    {
      id: "description",
      label: canvas?.description ? "Update description" : "Add description",
      action: () => setIsEditDescriptionModalOpen(true),
    },
    {
      id: "delete",
      label: "Delete",
      action: () => setIsDeleteCanvasModalOpen(true),
    },
  ];

  // Use "Unsaved Plan" when AI Copilot banner is visible and no canvas name is set
  const displayLabel =
    canvasName || (isDraftModeNotTemplates ? "Unsaved plan" : "untitled");

  return (
    <span data-testid="interactive-name">
      <AssetControl
        label={displayLabel}
        maxLength={NAME_MAX_LENGTH}
        onRename={handleSave}
        menuItems={menuItems}
        owner={{
          name: ownerName || "",
          avatarUrl: photoUrl,
        }}
        permissions={permissions}
      />
    </span>
  );
}

export default InteractiveName;
